/**
 * ProgramReminders — client-side push alerts for favourited programmes.
 *
 * Design notes:
 *   - We use the browser Notification API (works via the service worker when
 *     the PWA is installed). No server VAPID/web-push infrastructure yet.
 *   - Favourites live in localStorage keyed by station + program name + day
 *     + start time. Each favourite fires once, 5 minutes before its GMT
 *     start on that day.
 *   - Guards against duplicate firings via a "last_fired" timestamp keyed
 *     per week (yyyy-week#).
 *   - Silently no-ops when Notifications are unsupported or denied.
 */
import { useEffect, useRef } from 'react';
import {
  getScheduleForDay,
  getCurrentProgram,
} from '../utils/scheduleUtils';

const LS_FAVS = 'kioo_program_favs';
const LS_FIRED = 'kioo_program_reminders_fired';
const LEAD_MS = 5 * 60 * 1000;    // 5 minutes ahead
const TICK_MS = 30 * 1000;        // check every 30s

const DAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday',
];

// --- Public helpers used by Schedule page ---------------------------------

export const getFavourites = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_FAVS) || '[]');
  } catch (_e) {
    return [];
  }
};

const saveFavourites = (list) => {
  try {
    localStorage.setItem(LS_FAVS, JSON.stringify(list));
  } catch (_e) {
    /* ignore */
  }
};

const favKey = (fav) =>
  `${fav.station_slug || 'kioo'}|${fav.day}|${fav.start_time}|${fav.program_name}`;

export const isFavourited = (fav) => {
  const key = favKey(fav);
  return getFavourites().some((f) => favKey(f) === key);
};

export const toggleFavourite = async (fav) => {
  const list = getFavourites();
  const key = favKey(fav);
  const idx = list.findIndex((f) => favKey(f) === key);
  if (idx >= 0) {
    list.splice(idx, 1);
    saveFavourites(list);
    return false;
  }
  list.push({ ...fav, added_at: new Date().toISOString() });
  saveFavourites(list);
  // First favourite → politely ask for Notification permission so alerts
  // actually reach the listener.
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'default'
  ) {
    try {
      await Notification.requestPermission();
    } catch (_e) {
      /* user cancelled */
    }
  }
  return true;
};

// --- Scheduling core ------------------------------------------------------

const parseHHMM = (str) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((str || '').trim());
  if (!m) return null;
  return { h: parseInt(m[1], 10), m: parseInt(m[2], 10) };
};

/**
 * Compute the next UTC Date (>= now) that matches the given weekday name and
 * HH:MM UTC start time. Returns null when parsing fails.
 */
const nextOccurrenceUTC = (dayName, startTime) => {
  const dayIdx = DAYS.indexOf(dayName);
  const hm = parseHHMM(startTime);
  if (dayIdx < 0 || !hm) return null;
  const now = new Date();
  const nowDay = now.getUTCDay();
  let deltaDays = (dayIdx - nowDay + 7) % 7;
  const candidate = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + deltaDays,
      hm.h,
      hm.m,
      0,
      0
    )
  );
  if (candidate.getTime() <= now.getTime() && deltaDays === 0) {
    candidate.setUTCDate(candidate.getUTCDate() + 7);
  }
  return candidate;
};

const weekTag = (d) => {
  // ISO-ish yyyy-www tag good enough to reset firing state weekly.
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const days = Math.floor((d.getTime() - jan1.getTime()) / 86400000);
  const week = Math.floor((days + jan1.getUTCDay()) / 7);
  return `${d.getUTCFullYear()}-${week}`;
};

const readFired = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_FIRED) || '{}');
  } catch (_e) {
    return {};
  }
};
const writeFired = (obj) => {
  try {
    localStorage.setItem(LS_FIRED, JSON.stringify(obj));
  } catch (_e) {
    /* ignore */
  }
};

const showNotification = (title, body, icon) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistration) {
      navigator.serviceWorker
        .getRegistration('/app/')
        .then((reg) => {
          if (reg) {
            reg.showNotification(title, {
              body,
              icon: icon || '/logo192.png',
              badge: '/logo192.png',
              tag: 'kioo-program-reminder',
              vibrate: [80, 40, 80],
            });
          } else {
            new Notification(title, { body, icon: icon || '/logo192.png' });
          }
        })
        .catch(() => new Notification(title, { body, icon: icon || '/logo192.png' }));
    } else {
      new Notification(title, { body, icon: icon || '/logo192.png' });
    }
  } catch (_e) {
    /* ignore */
  }
};

// Best-effort match of the favourite against today's current program so we
// can show the "🔴 On air" state even without a real push channel.
const matchesNowOnAir = (fav) => {
  const info = getCurrentProgram();
  if (!info?.current) return false;
  const now = info.current;
  const name = now.name || now.Program;
  const start = now.startTime || now['Start Time'];
  return name === fav.program_name && start === fav.start_time;
};

const ProgramReminders = () => {
  const timerRef = useRef(null);
  useEffect(() => {
    const tick = () => {
      const favs = getFavourites();
      if (!favs.length) return;
      const fired = readFired();
      const nowMs = Date.now();
      let dirty = false;
      for (const fav of favs) {
        if (!fav?.program_name || !fav?.start_time || !fav?.day) continue;
        const when = nextOccurrenceUTC(fav.day, fav.start_time);
        if (!when) continue;
        const key = `${favKey(fav)}|${weekTag(when)}`;
        if (fired[key]) continue;
        const diff = when.getTime() - nowMs;
        if (diff > 0 && diff <= LEAD_MS) {
          const mins = Math.max(1, Math.round(diff / 60000));
          showNotification(
            `Kioo Radio · ${fav.program_name}`,
            `Starts in ${mins} min · ${fav.day} ${fav.start_time} GMT · tap to listen`
          );
          fired[key] = nowMs;
          dirty = true;
        } else if (diff <= 0 && diff > -60 * 1000 && matchesNowOnAir(fav)) {
          showNotification(
            `🔴 ${fav.program_name} is on air`,
            `Tap to open Kioo Radio and listen live`
          );
          fired[key] = nowMs;
          dirty = true;
        }
      }
      // Purge old fired entries so the store doesn't grow unbounded.
      const nowTag = weekTag(new Date());
      for (const k of Object.keys(fired)) {
        const tag = k.split('|').pop();
        if (tag !== nowTag) {
          delete fired[k];
          dirty = true;
        }
      }
      if (dirty) writeFired(fired);
    };
    // Fire a tick right away so any imminent reminder isn't missed on load.
    tick();
    timerRef.current = setInterval(tick, TICK_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  return null;
};

export default ProgramReminders;

// Utility for the Schedule page to reference the current-day schedule
// without importing scheduleUtils in two places.
export const _getScheduleForDay = getScheduleForDay;
